import { useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarDays, UserMinus, UsersRound } from 'lucide-react'

import { dashboardApi } from '@/services/dashboard'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { cn, formatDate } from '@/lib/utils'
import type { DashboardUpcomingWorkdayScheduleItem, WorkerDayAvailabilityItem, WorkerDayLeaveItem } from '@/types'

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

const toInputDate = (date: Date) => {
  const tzOffset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - tzOffset * 60 * 1000)
  return local.toISOString().slice(0, 10)
}

export default function ScheduleWorkersPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/job/realtime')) {
      return '/job/realtime'
    }
    if (pathname.startsWith('/timeline')) {
      return '/timeline'
    }
    if (pathname.startsWith('/workdays')) {
      return '/workdays'
    }
    return '/jobs/scheduling'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const [start, setStart] = useState(() => toInputDate(new Date()))
  const [end, setEnd] = useState(() => {
    const value = new Date()
    value.setDate(value.getDate() + 6)
    return toInputDate(value)
  })
  const [locationFilter, setLocationFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const queryKey = useMemo(() => ['job-schedule-workers-overview', start, end], [start, end])
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => dashboardApi.getScheduleWorkersOverview(start, end),
  })

  const schedules = data?.upcoming_workday_schedules || []

  const locationOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.location_name || '').trim()
        return raw || 'Unknown location'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const categoryOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.category_name || '').trim()
        return raw || 'Unknown category'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const filteredSchedules = useMemo(() => {
    const locationNeedle = locationFilter.trim().toLowerCase()
    const categoryNeedle = categoryFilter.trim().toLowerCase()

    return schedules.filter((entry) => {
      const locationValue = (String(entry.location_name || '').trim() || 'Unknown location').toLowerCase()
      const categoryValue = (String(entry.category_name || '').trim() || 'Unknown category').toLowerCase()

      const locationMatches = !locationNeedle || locationValue.includes(locationNeedle)
      const categoryMatches = !categoryNeedle || categoryValue.includes(categoryNeedle)

      return locationMatches && categoryMatches
    })
  }, [schedules, locationFilter, categoryFilter])

  const sevenDays = buildSevenDayWindow(start)
  const availabilityLookup = useMemo(
    () => buildWorkerAvailabilityLookup(data?.worker_day_availability || []),
    [data?.worker_day_availability],
  )
  const leaveLookup = useMemo(
    () => buildWorkerLeaveLookup(data?.worker_day_leaves || []),
    [data?.worker_day_leaves],
  )
  const weeklyRows = buildWeeklyRows(filteredSchedules, sevenDays, availabilityLookup, leaveLookup)
  const workerCandidates = collectWorkerCandidates(weeklyRows)
  const highlightAssignment = data?.unassigned_assignments?.[0]
  const outOfAvailabilityCount = weeklyRows.reduce(
    (count, row) => count + row.cells.filter((cell) => cell.isOutsideAvailability).length,
    0,
  )
  const hasOutOfAvailabilityAllocations = outOfAvailabilityCount > 0

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <h1 className="text-2xl font-semibold">Schedule Workers</h1>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Unable to load schedule workers snapshot.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-white p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] p-2">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <Button
              key={tab.value}
              type="button"
              variant="ghost"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold',
                isActive
                  ? 'bg-[#169b62] text-white hover:bg-[#118753] hover:text-white'
                  : 'text-[#355445] hover:bg-[#e8f4ee]'
              )}
            >
              {tab.label}
            </Button>
          )
        })}
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-[34px] font-semibold tracking-tight text-[#20352b]">Schedule Workers for Future Jobs</h1>
          <p className="text-[12px] text-[#718078]">
            Plan ahead, assign workers and avoid clashes.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button onClick={() => refetch()} disabled={isFetching} className="h-9 rounded-md bg-[#169b62] px-4 text-xs font-semibold hover:bg-[#118753]">
            + New Job
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricTile icon={<CalendarDays className="h-4 w-4" />} tone="blue" value={data.summary.total_workday_schedules_count} label="Jobs This Week" />
        <MetricTile icon={<UsersRound className="h-4 w-4" />} tone="green" value={data.summary.assigned_workday_schedules_count} label="Workers Assigned" />
        <MetricTile icon={<UserMinus className="h-4 w-4" />} tone="rose" value={data.summary.unassigned_assignments_count} label="Unassigned" />
        <MetricTile icon={<AlertTriangle className="h-4 w-4" />} tone="amber" value={data.summary.clashed_schedules_count} label="Conflicts" />
      </div>

      <div className="grid gap-2 lg:grid-cols-4">
        <Input type="date" value={start} onChange={(event) => setStart(event.target.value)} className="h-9 border-[#dfeae3] text-xs" />
        <Input
          type="search"
          list="schedule-location-options"
          value={locationFilter}
          onChange={(event) => setLocationFilter(event.target.value)}
          placeholder="All Locations"
          className="h-9 border-[#dfeae3] text-xs"
        />
        <datalist id="schedule-location-options">
          {locationOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <Input
          type="search"
          list="schedule-category-options"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
          placeholder="All Categories"
          className="h-9 border-[#dfeae3] text-xs"
        />
        <datalist id="schedule-category-options">
          {categoryOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <Input type="date" value={end} onChange={(event) => setEnd(event.target.value)} className="h-9 border-[#dfeae3] text-xs" />
      </div>

      <Card className="border-[#dfeae3] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base text-[#20352b]">{formatDate(data.period.start)} - {formatDate(data.period.end)}</CardTitle>
            <CardDescription className="text-xs text-[#718078]">{data.summary.total_workday_schedules_count} scheduled entries</CardDescription>
          </div>
          <Badge className="bg-[#ddf6e7] text-[#087a49] hover:bg-[#ddf6e7]">Timeline</Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {outOfAvailabilityCount > 0 ? (
            <div className="rounded-md border border-[#f4c8cc] bg-[#fde9eb] px-3 py-2 text-xs text-[#a63c46]">
              <p className="font-semibold">Alert: {outOfAvailabilityCount} allocation{outOfAvailabilityCount > 1 ? 's are' : ' is'} outside worker availability.</p>
            </div>
          ) : null}
          <div className="overflow-x-auto pb-1">
            <div className="min-w-[980px]">
              <div className="mb-2 grid grid-cols-12 gap-2 rounded-md bg-[#f6f8f7] p-2">
                <div className="col-span-2 text-[11px] font-semibold text-[#4d5f56]">Worker</div>
                {sevenDays.map((day) => (
                  <div key={day} className="col-span-1 text-[11px] font-semibold text-[#4d5f56]">
                    {formatCompactDayLabel(day)}
                  </div>
                ))}
                <div className="col-span-3 text-[11px] font-semibold text-[#4d5f56]">Notes</div>
              </div>

              {weeklyRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No schedules in this window.</p>
              ) : (
                weeklyRows.map((row) => (
                  <div key={row.worker} className="mb-2 grid grid-cols-12 gap-2 rounded-lg border border-[#eef3ef] p-2">
                    <div className="col-span-2 rounded-md p-1">
                      {row.workerId ? (
                        <Link to={`/workers/${row.workerId}`} className="text-sm font-semibold text-[#20352b] underline-offset-2 hover:underline">
                          {row.worker}
                        </Link>
                      ) : (
                        <p className="text-sm font-semibold text-[#20352b]">{row.worker}</p>
                      )}
                      <p className="text-[11px] text-[#718078]">{row.role}</p>
                    </div>
                    {row.cells.map((cell) => (
                      cell.isAvailable ? (
                        <div
                          key={cell.key}
                          className={`col-span-1 rounded-md px-2 py-2 ${cell.tone}`}
                        >
                          <p className={`text-[10px] font-medium ${cell.availabilityLabel === 'Unavailable' ? 'text-[#b7515a]' : 'text-[#087a49]'}`}>{cell.availabilityLabel}</p>
                          <p className={`mt-1 text-[9px] ${cell.availabilityLabel === 'Unavailable' ? 'text-[#b7515a]' : 'text-[#087a49]'}`}>{cell.availabilityText}</p>
                        </div>
                      ) : (
                        <div key={cell.key} className="col-span-1 space-y-1">
                          <div className={`rounded-md px-2 py-2 ${cell.tone}`}>
                            <p className="text-[9px] font-medium text-white">{cell.time}</p>
                            {cell.jobId ? (
                              <Link
                                to={`/jobs/${cell.jobId}`}
                                className="mt-1 line-clamp-2 text-[10px] text-white underline-offset-2 hover:underline"
                              >
                                {cell.title}
                              </Link>
                            ) : (
                              <p className="mt-1 line-clamp-2 text-[10px] text-white">{cell.title}</p>
                            )}
                          </div>
                          <p className={`text-[9px] ${cell.isOutsideAvailability ? 'font-semibold text-[#b7515a]' : 'text-[#087a49]'}`}>
                            Avail: {cell.availabilityText}
                          </p>
                          {cell.isOutsideAvailability ? (
                            <p className="text-[9px] font-semibold text-[#b7515a]">{cell.isOnLeave ? 'ALERT: Worker on leave' : 'ALERT: Outside availability'}</p>
                          ) : null}
                        </div>
                      )
                    ))}
                    <div className="col-span-3 rounded-md bg-[#f8fbf9] px-2 py-2 text-[10px] text-[#6c7d74]">
                      {row.notes}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </CardContent>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-[#20352b]">Job Details</CardTitle>
            <span className="text-xs font-medium text-[#169b62]">Edit</span>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-base font-semibold text-[#20352b]">{highlightAssignment?.job_title || 'Office Cleaning'}</p>
            <p className="text-xs text-[#718078]">{highlightAssignment?.role_label || 'Standard'}</p>
            <p className="pt-2 text-xs text-[#718078]">
              {highlightAssignment?.scheduled_start ? formatDate(highlightAssignment.scheduled_start, { hour: '2-digit', minute: '2-digit' }) : 'Schedule not set'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-[#20352b]">Assign Worker</CardTitle>
            <span className="text-xs text-[#3f7bc0]">Best match</span>
          </CardHeader>
          <CardContent className="space-y-1">
            {workerCandidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No worker suggestions available.</p>
            ) : (
              workerCandidates.map((candidate) => (
                <div key={`${candidate.name}-${candidate.workerId ?? 'no-id'}`} className="flex items-center justify-between py-1 text-xs">
                  {candidate.workerId ? (
                    <Link to={`/workers/${candidate.workerId}`} className="text-[#20352b] underline-offset-2 hover:underline">
                      ○ {candidate.name}
                    </Link>
                  ) : (
                    <span className="text-[#20352b]">○ {candidate.name}</span>
                  )}
                  <span className={candidate.hasOutsideAvailability ? 'text-[#b7515a] font-semibold' : 'text-[#169b62]'}>
                    {candidate.hasLeave ? 'On leave' : (candidate.hasOutsideAvailability ? 'Outside availability' : 'Available')}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" className="border-[#dfeae3] text-xs">Cancel</Button>
        {hasOutOfAvailabilityAllocations ? (
          <Button disabled className="cursor-not-allowed bg-[#9fa8a3] text-xs text-white hover:bg-[#9fa8a3]">
            Schedule Job (Blocked)
          </Button>
        ) : (
          <Button asChild className="bg-[#169b62] text-xs hover:bg-[#118753]"><Link to="/assignments">Schedule Job</Link></Button>
        )}
      </div>
    </div>
  )
}

function MetricTile({
  icon,
  tone,
  value,
  label,
}: {
  icon: ReactNode
  tone: 'blue' | 'green' | 'rose' | 'amber'
  value: number
  label: string
}) {
  const toneClass = {
    blue: 'bg-[#e7f3fd] text-[#2d77bb]',
    green: 'bg-[#ddf6e7] text-[#148653]',
    rose: 'bg-[#fde9eb] text-[#b7515a]',
    amber: 'bg-[#fff2de] text-[#ae7a2c]',
  }

  return (
    <Card className="border-[#dfeae3] shadow-none">
      <CardContent className="flex items-center gap-3 p-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[tone]}`}>
          {icon}
        </div>
        <div>
          <p className="text-[26px] leading-none text-[#20352b]">{value}</p>
          <p className="mt-1 text-[11px] text-[#718078]">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function buildWeeklyRows(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  sevenDays: string[],
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
) {
  const byWorker = new Map<string, DashboardUpcomingWorkdayScheduleItem[]>()
  entries.forEach((entry) => {
    const key = entry.worker_name || 'Unassigned'
    const rows = byWorker.get(key) ?? []
    rows.push(entry)
    byWorker.set(key, rows)
  })

  return Array.from(byWorker.entries()).slice(0, 8).map(([worker, rows]) => {
    const workerId = rows.find((entry) => entry.worker_id != null)?.worker_id ?? null
    const scheduled = sevenDays.map((day, dayIndex) => {
      const availabilityText = resolveWorkerDayAvailability(availabilityLookup, workerId, day)
      const isOnLeave = resolveWorkerOnLeave(leaveLookup, workerId, day)
      const items = rows.filter((entry) => resolveEntryDay(entry) === day)
      if (items.length === 0) {
        const isUnavailable = availabilityText === 'Unavailable' || isOnLeave
        return {
          key: `${worker}-${day}-available`,
          time: '',
          title: '',
          availabilityLabel: isOnLeave ? 'On Leave' : (isUnavailable ? 'Unavailable' : 'Available'),
          availabilityText: isOnLeave ? 'On Leave' : availabilityText,
          isAvailable: true,
          isOutsideAvailability: false,
          isOnLeave,
          tone: isUnavailable ? 'bg-[#fde9eb]' : 'bg-[#ddf6e7]',
        }
      }

      const primary = pickDisplayEntry(items)
      const time = formatEntryTimeRange(primary)
      const isOutsideAvailability = isOnLeave || isScheduleOutsideAvailabilityWindow(time, availabilityText)
      const extraLabel = items.length > 1 ? ` +${items.length - 1}` : ''
      return {
        key: `${worker}-${day}-${dayIndex}`,
        jobId: primary.job_id,
        time,
        title: `${primary.job_title || 'Scheduled work'}${extraLabel}`,
        availabilityLabel: '',
        availabilityText: isOnLeave ? 'On Leave' : availabilityText,
        isAvailable: false,
        isOutsideAvailability,
        isOnLeave,
        tone: isOutsideAvailability ? 'bg-[#cb3d4d]' : resolveScheduleTone(primary),
      }
    })

    return {
      worker,
      workerId,
      role: 'Field Worker',
      cells: scheduled,
      notes: rows.some((entry) => entry.status === 'clashed') ? 'Potential clash detected' : 'Ready for scheduling',
    }
  })
}

function buildWorkerAvailabilityLookup(items: WorkerDayAvailabilityItem[]) {
  const lookup = new Map<string, string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate) {
      return
    }

    const key = `${workerId}|${workDate}`
    if (item.is_available === false) {
      lookup.set(key, '--:--')
      return
    }

    const from = String(item.from_time || '').trim()
    const to = String(item.to_time || '').trim()
    if (!from || !to) {
      if (!lookup.has(key)) {
        lookup.set(key, '--:--')
      }
      return
    }

    const nextValue = `${formatHourMinute(from)}-${formatHourMinute(to)}`
    const existing = lookup.get(key)
    if (!existing || existing === 'Available') {
      lookup.set(key, nextValue)
      return
    }
    if (existing !== 'Unavailable') {
      lookup.set(key, `${existing}, ${nextValue}`)
    }
  })

  return lookup
}

function buildWorkerLeaveLookup(items: WorkerDayLeaveItem[]) {
  const lookup = new Set<string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate || item.is_on_leave !== true) {
      return
    }

    lookup.add(`${workerId}|${workDate}`)
  })

  return lookup
}

function resolveWorkerOnLeave(lookup: Set<string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return false
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.has(key)
}

function isScheduleOutsideAvailabilityWindow(scheduleTime: string, availabilityText: string) {
  const scheduleRange = parseHourMinuteRange(scheduleTime)
  if (!scheduleRange) {
    return false
  }

  const availabilityRaw = String(availabilityText || '').trim()
  if (!availabilityRaw || availabilityRaw === '--:--') {
    return false
  }

  if (availabilityRaw.toLowerCase() === 'unavailable') {
    return true
  }

  const availabilityRanges = availabilityRaw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)

  if (availabilityRanges.length === 0) {
    return false
  }

  return !availabilityRanges.some(([availableStart, availableEnd]) => {
    const [scheduledStart, scheduledEnd] = scheduleRange
    return scheduledStart >= availableStart && scheduledEnd <= availableEnd
  })
}

function parseHourMinuteRange(value: string): [number, number] | null {
  const raw = String(value || '').trim()
  const [startRaw, endRaw] = raw.split('-').map((part) => part.trim())
  if (!startRaw || !endRaw) {
    return null
  }

  const start = parseHourMinuteToMinutes(startRaw)
  const end = parseHourMinuteToMinutes(endRaw)
  if (start == null || end == null || end <= start) {
    return null
  }

  return [start, end]
}

function parseHourMinuteToMinutes(value: string): number | null {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

function formatHourMinute(value: string) {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return raw
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

function resolveWorkerDayAvailability(lookup: Map<string, string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return '--:--'
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.get(key) || '--:--'
}

function buildSevenDayWindow(startDate: string) {
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) {
    return []
  }

  const days: string[] = []
  for (let index = 0; index < 7; index += 1) {
    const day = new Date(start)
    day.setDate(start.getDate() + index)
    days.push(toInputDate(day))
  }
  return days
}

function resolveEntryDay(entry: DashboardUpcomingWorkdayScheduleItem) {
  const explicitDay = String(entry.work_date || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDay)) {
    return explicitDay
  }

  // Fall back to planned_start UTC day when work_date is unavailable.
  const plannedStart = String(entry.planned_start || '').trim()
  if (!plannedStart) {
    return ''
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(plannedStart)) {
    return plannedStart.slice(0, 10)
  }

  const parsed = new Date(plannedStart)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return parsed.toISOString().slice(0, 10)
}

function formatEntryTimeRange(entry: DashboardUpcomingWorkdayScheduleItem) {
  const normalized = formatRangeFromDateTimes(entry.planned_start, entry.planned_end)
  if (normalized) {
    return normalized
  }

  const start = entry.planned_start_local || entry.planned_start
  const end = entry.planned_end_local || entry.planned_end
  return `${toShortTime(start)}-${toShortTime(end)}`
}

function toShortTime(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) {
    return '--'
  }

  // Date-only values do not include a schedule time; avoid coercing to midnight.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return '--'
  }

  const directMatch = raw.match(/\b(\d{2}:\d{2})/)
  if (directMatch?.[1]) {
    return directMatch[1]
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return raw
  }

  return parsed.toISOString().slice(11, 16)
}

function pickDisplayEntry(items: DashboardUpcomingWorkdayScheduleItem[]) {
  const withNonMidnight = items.find((item) => {
    const label = formatEntryTimeRange(item)
    return label !== '00:00-00:00' && label !== '-----'
  })
  return withNonMidnight || items[0]
}

function formatRangeFromDateTimes(startValue?: string | null, endValue?: string | null) {
  const start = parseScheduleDate(startValue)
  const end = parseScheduleDate(endValue)
  if (!start || !end) {
    return null
  }

  const startMs = start.getTime()
  const endMs = end.getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })

  return `${formatter.format(start)}-${formatter.format(end)}`
}

function parseScheduleDate(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function collectWorkerCandidates(rows: ReturnType<typeof buildWeeklyRows>) {
  return rows
    .filter((row) => row.worker && row.worker !== 'Unassigned')
    .map((row) => ({
      name: row.worker,
      workerId: row.workerId,
      hasOutsideAvailability: row.cells.some((cell) => cell.isOutsideAvailability),
      hasLeave: row.cells.some((cell) => cell.isOnLeave),
    }))
    .slice(0, 4)
}

function formatCompactDayLabel(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
  }).replace(',', '')
}

function resolveScheduleTone(entry: DashboardUpcomingWorkdayScheduleItem) {
  if (!entry.worker_name || entry.worker_name === 'Unassigned') {
    return 'bg-[#cb3d4d]'
  }

  if (entry.status === 'clashed') {
    return 'bg-[#cf8a1a]'
  }

  return 'bg-[#2f8fdd]'
}
